import {PHONE_NUMBER_REGEX} from './phoneValidation'
// EOR Utility functions and constants
// Based on SHR-FE implementation

export const eorTypes = ['Corporation', 'LLC', 'Partnership', 'Not Specified']

export const bankAccountTypes = [
  'Checking',
  'Savings',
  // 'Money Market'
]

// only manual upload is allowed for now
export const achFileDeliveryMethodTypes = [
  // 'SFTP',
  'Manual Upload',
  // 'Email', 'FTP'
]

export const irsFilingFrequencyTypes = ['Monthly', 'Quarterly', 'Yearly']

export const payrollCycles = ['Weekly', 'Bi-Weekly', 'Semi-Monthly', 'Monthly']

export const weekDays = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

export const overtimeRules = ['Daily OT', 'Weekly OT', 'Daily & Weekly OT', 'No OT']

export const payDateLagOptions = ['Same Day', 'Weekly', 'Bi-Weekly', 'Monthly']

export const positivePayOptions = ['Yes', 'No']

// US States for EOR operations
export const US_STATES = [
  {code: 'AL', name: 'Alabama'},
  {code: 'AK', name: 'Alaska'},
  {code: 'AZ', name: 'Arizona'},
  {code: 'AR', name: 'Arkansas'},
  {code: 'CA', name: 'California'},
  {code: 'CO', name: 'Colorado'},
  {code: 'CT', name: 'Connecticut'},
  {code: 'DE', name: 'Delaware'},
  {code: 'FL', name: 'Florida'},
  {code: 'GA', name: 'Georgia'},
  {code: 'HI', name: 'Hawaii'},
  {code: 'ID', name: 'Idaho'},
  {code: 'IL', name: 'Illinois'},
  {code: 'IN', name: 'Indiana'},
  {code: 'IA', name: 'Iowa'},
  {code: 'KS', name: 'Kansas'},
  {code: 'KY', name: 'Kentucky'},
  {code: 'LA', name: 'Louisiana'},
  {code: 'ME', name: 'Maine'},
  {code: 'MD', name: 'Maryland'},
  {code: 'MA', name: 'Massachusetts'},
  {code: 'MI', name: 'Michigan'},
  {code: 'MN', name: 'Minnesota'},
  {code: 'MS', name: 'Mississippi'},
  {code: 'MO', name: 'Missouri'},
  {code: 'MT', name: 'Montana'},
  {code: 'NE', name: 'Nebraska'},
  {code: 'NV', name: 'Nevada'},
  {code: 'NH', name: 'New Hampshire'},
  {code: 'NJ', name: 'New Jersey'},
  {code: 'NM', name: 'New Mexico'},
  {code: 'NY', name: 'New York'},
  {code: 'NC', name: 'North Carolina'},
  {code: 'ND', name: 'North Dakota'},
  {code: 'OH', name: 'Ohio'},
  {code: 'OK', name: 'Oklahoma'},
  {code: 'OR', name: 'Oregon'},
  {code: 'PA', name: 'Pennsylvania'},
  {code: 'RI', name: 'Rhode Island'},
  {code: 'SC', name: 'South Carolina'},
  {code: 'SD', name: 'South Dakota'},
  {code: 'TN', name: 'Tennessee'},
  {code: 'TX', name: 'Texas'},
  {code: 'UT', name: 'Utah'},
  {code: 'VT', name: 'Vermont'},
  {code: 'VA', name: 'Virginia'},
  {code: 'WA', name: 'Washington'},
  {code: 'WV', name: 'West Virginia'},
  {code: 'WI', name: 'Wisconsin'},
  {code: 'WY', name: 'Wyoming'},
]

// Utility functions
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

// Regex to allow only numbers and dash in EIN input
export const EIN_INPUT_FILTER_REGEX = /[^\d-]/g

/**
 * Format EIN as XX-XXXXXXX
 * Auto-adds dash after 2nd digit while typing
 */
export const formatEIN = (ein: string): string => {
  // Remove all non-digit characters
  const cleaned = ein.replace(/\D/g, '')

  // If less than 2 digits, return as-is
  if (cleaned.length < 2) {
    return cleaned
  }

  // Add dash after first 2 digits when there are more than 2: XX-XXXXXXX
  return `${cleaned.slice(0, 2)}${cleaned.length > 2 ? '-' : ''}${cleaned.slice(2, 9)}`
}

export const validateEIN = (ein: string): boolean => {
  const einRegex = /^\d{2}-\d{7}$/
  return einRegex.test(ein)
}

/**
 * Validates phone number in either US format (XXX) XXX-XXXX or international format +X XXXXXXXXXX
 */
export const validatePhoneNumber = (phone: string): boolean => {
  return PHONE_NUMBER_REGEX.test(phone)
}

export const getStateNameByCode = (code: string): string => {
  const state = US_STATES.find((s) => s.code === code)
  return state ? state.name : code
}

export const getStateCodeByName = (name: string): string => {
  const state = US_STATES.find((s) => s.name === name)
  return state ? state.code : name
}

// File validation utilities
export const validateFileSize = (file: File, maxSizeMB: number = 500): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  return file.size <= maxSizeBytes
}

export const validateFileType = (file: File, allowedTypes: string[]): boolean => {
  return (
    allowedTypes.includes(file.type) ||
    allowedTypes.some((type) => file.name.toLowerCase().endsWith(type.replace('*', '')))
  )
}

export const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.')
  if (lastDotIndex <= 0) {
    return ''
  }
  return filename.slice(lastDotIndex + 1)
}

// Form validation helpers
export const isValidSUTARate = (rate: number): boolean => {
  return rate >= 0 && rate <= 100
}

export const isValidZipCode = (zipCode: string): boolean => {
  const zipRegex = /^\d{5}(-\d{4})?$/
  return zipRegex.test(zipCode)
}

// Date utilities for EOR
export const formatDateForAPI = (date: Date | string): string => {
  if (typeof date === 'string') return date
  return date.toISOString().split('T')[0]
}

export const parseAPIDate = (dateString: string): Date => {
  return new Date(dateString)
}

// EOR status utilities
export const getEorStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'active':
      return '#4caf50'
    case 'archived':
      return '#ff9800'
    case 'inactive':
      return '#f44336'
    default:
      return '#757575'
  }
}

export const getEorStatusLabel = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'Active'
    case 'archived':
      return 'Archived'
    case 'inactive':
      return 'Inactive'
    default:
      return status
  }
}
